import { MapPin } from 'lucide-react';

import { MediaFrame, Parallax, PointerParallax, ScrollReveal, SplitReveal } from '@/components/motion';
import {
  CAMPUS_EVENTS,
  CAMPUS_EVENTS_ADDRESS,
  CAMPUS_EVENTS_LEDE,
  CAMPUS_MOMENTS,
  GRADUATION_NOTE,
} from '@/data/current-students';

/**
 * CampusThalwilEvents (/current-students — section 3) — "Recent moments on campus."
 *
 * A refined vertical timeline of CAMPUS_EVENTS (most-recent-first, all past —
 * presented in past tense as recent highlights, never as upcoming and with no
 * register/RSVP call to action): each row pairs the event's Icon in a navy
 * chip with a small kind badge, a muted tabular-nums date, a display title and
 * its description. Below, a "From the campus" block renders the two
 * CAMPUS_MOMENTS as verbatim-quote cards. A closing footer note combines
 * GRADUATION_NOTE with CAMPUS_EVENTS_ADDRESS, the on-site venue for these
 * gatherings. An optional campus photo sits alongside the intro for texture.
 *
 * Motion: SplitReveal headline, ScrollReveal staggers down the timeline and
 * across the quote cards, Parallax + PointerParallax depth on the photo — all
 * reduced-motion safe. Every institutional fact comes verbatim from
 * '@/data/current-students'; nothing is invented.
 */
export function CampusThalwilEvents() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede · campus photo ──────────────────────────────── */}
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <header className="max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Campus Thalwil events
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              Recent moments on campus.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                {CAMPUS_EVENTS_LEDE}
              </p>
            </ScrollReveal>
          </header>

          <Parallax speed={0.1} className="mx-auto w-full max-w-md lg:max-w-none">
            <PointerParallax strength={14}>
              <div data-parallax data-depth="0.6">
                <MediaFrame
                  image="/media/images/campus/ibas-thalwil-building-exterior.webp"
                  alt="Exterior view of the copper-panelled IBAS Thalwil campus building, where on-site events are hosted."
                  aspect="aspect-[4/5]"
                  overlay={30}
                  rounded="rounded-2xl"
                  position="50% 40%"
                />
              </div>
            </PointerParallax>
          </Parallax>
        </div>

        {/* ── Timeline of recent on-site events ──────────────────────────── */}
        <ScrollReveal stagger={0.1} className="mt-16 space-y-6 border-t border-border pt-12">
          {CAMPUS_EVENTS.map((event) => (
            <div
              key={event.title}
              className="flex gap-5 rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                <event.Icon aria-hidden className="size-5" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center rounded-full border border-border bg-muted/30 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    {event.kind}
                  </span>
                  <span className="text-xs tracking-wide text-muted-foreground tabular-nums">
                    {event.date}
                  </span>
                </div>
                <h3 className="mt-2 font-display text-xl leading-tight font-semibold text-foreground">
                  {event.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {event.description}
                </p>
              </div>
            </div>
          ))}
        </ScrollReveal>

        {/* ── From the campus — verbatim quotes ──────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            From the campus
          </p>
          <ScrollReveal stagger={0.1} className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {CAMPUS_MOMENTS.map((moment) => (
              <blockquote
                key={moment.title}
                className="h-full rounded-2xl border border-border bg-muted/30 p-7"
              >
                <p className="font-display text-lg leading-snug text-foreground">
                  “{moment.quote}”
                </p>
                <footer className="mt-4 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{moment.title}</span>
                  <span className="mx-2">·</span>
                  <span className="tabular-nums">{moment.date}</span>
                </footer>
              </blockquote>
            ))}
          </ScrollReveal>
        </div>

        {/* ── Footer note — venue for on-site events ─────────────────────── */}
        <ScrollReveal from="up" delay={0.1}>
          <div className="mt-12 flex items-start gap-3 border-t border-border pt-8 text-sm leading-relaxed text-muted-foreground">
            <MapPin aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-gold" />
            <p>
              {GRADUATION_NOTE} On-site events are hosted at the Thalwil campus,{' '}
              {CAMPUS_EVENTS_ADDRESS}.
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
